// Phase 1: Hook & Low-Friction Entry (merged flow — 2 screens, plus the
// /2026-only s0_hero intro that lands BEFORE s1 on the 2026 funnel).
// s0_hero: full-bleed photo hero with award + ratings badges (/2026 only)
// s1: Welcome entry (age by default, screen-time range for /2026)
// s4: Social proof + safe-space trust row (merged)
import { useEffect, useState } from 'react';
import { Screen, OpalWordmark, OptionTile, PrimaryButton, StickyFooter } from './shared.jsx';
import { AppIconFor, OpalAppIcon, PressLogo } from './brand.jsx';
import { screenTimeBucket } from './math.js';
import { Experiment, Variant, Fallback } from './lib/experiments';
import cfg from './config.js';

// Active funnel at module load — used to gate render-time experiment wraps so
// experiments only fire for users on the funnel they're scoped to. The slug
// matches funnels/index.js (e.g. '2026', 'adhd', 'families', 'screentime').
// `window.__opalFunnel` is set in app.jsx at top-level.
function currentFunnelSlug() {
  return (typeof window !== 'undefined' && window.__opalFunnel?.slug) || null;
}



const LANDING_APP_OPTIONS = [
  'Instagram', 'TikTok', 'YouTube', 'Twitter/X', 'Reddit', 'Snapchat',
  'Facebook', 'Netflix', 'Messages', 'Discord', 'Pinterest', 'LinkedIn', 'Other',
];

const SCREEN_TIME_ASSET_BASE = '/assets/screen-time-range/gpt-hourglass';
const SCREEN_TIME_RANGE_OPTIONS = [
  { k: '0-2h', label: '0-2 hours', caption: 'Light', hours: 2, icon: `${SCREEN_TIME_ASSET_BASE}-0-2h` },
  { k: '2-4h', label: '2-4 hours', caption: 'Moderate', hours: 4, icon: `${SCREEN_TIME_ASSET_BASE}-2-4h` },
  { k: '4-6h', label: '4-6 hours', caption: 'Heavy', hours: 6, icon: `${SCREEN_TIME_ASSET_BASE}-4-6h` },
  { k: '6h+', label: '6+ hours', caption: 'Very heavy', hours: 8, icon: `${SCREEN_TIME_ASSET_BASE}-6-plus` },
];

const AGE_PORTRAIT_BASE = '/assets/portraits/opal-portrait';

const AGE_CARDS = [
  { k: '18-24', label: 'Age: 18-24', img: `${AGE_PORTRAIT_BASE}-18-24-male` },
  { k: '25-34', label: 'Age: 25-34', img: `${AGE_PORTRAIT_BASE}-25-34-female` },
  { k: '35-44', label: 'Age: 35-44', img: `${AGE_PORTRAIT_BASE}-35-44-male` },
  { k: '45+',   label: 'Age: 45+',   img: `${AGE_PORTRAIT_BASE}-45-plus-female` },
];

function ScreenTimeRangeCard({ card, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={card.label}
      style={{
        minHeight: 112,
        borderRadius: 22,
        border: '1px solid rgba(255,255,255,0.12)',
        background: `
          linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.025)),
          radial-gradient(120px 92px at 10% 45%, rgba(212,255,156,0.15), transparent 70%)`,
        padding: '13px 11px 13px 8px',
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 14px 34px rgba(0,0,0,0.22)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        textAlign: 'left',
        transition: 'transform 140ms var(--ease-out-soft), border-color 140ms, background 140ms',
      }}
      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.96)'}
      onMouseUp={(e) => e.currentTarget.style.transform = ''}
      onMouseLeave={(e) => e.currentTarget.style.transform = ''}
    >
      <picture style={{ width: 58, height: 58, display: 'block', flexShrink: 0 }}>
        <source srcSet={`${card.icon}.avif`} type="image/avif" />
        <source srcSet={`${card.icon}.webp`} type="image/webp" />
        <img
          src={`${card.icon}.png`}
          alt=""
          width="64"
          height="64"
          loading="eager"
          decoding="async"
          style={{
            width: 58,
            height: 58,
            objectFit: 'contain',
            display: 'block',
            filter: 'drop-shadow(0 10px 18px rgba(158,249,255,0.15))',
          }}
        />
      </picture>
      <span style={{ display: 'flex', minWidth: 0, flexDirection: 'column', gap: 3 }}>
        <span style={{
          color: 'var(--fg1)',
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 15,
          lineHeight: '18px',
          letterSpacing: '-0.02em',
        }}>{card.label}</span>
        <span style={{
          color: 'var(--fg3)',
          fontSize: 11,
          lineHeight: '13px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}>{card.caption}</span>
      </span>
    </button>
  );
}

function ImageOptionCard({ card, onClick }) {
  return (
    <button onClick={onClick} style={{
      position: 'relative',
      aspectRatio: '2 / 3',
      borderRadius: 22,
      border: '1px solid var(--ios-hairline)',
      background: `
        linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01)),
        linear-gradient(135deg, rgba(212,255,156,0.12), rgba(158,249,255,0.08))`,
      padding: 0, overflow: 'hidden', cursor: 'pointer',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
      transition: 'transform 140ms var(--ease-out-soft), box-shadow 140ms',
    }}
      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
      onMouseUp={(e) => e.currentTarget.style.transform = ''}
      onMouseLeave={(e) => e.currentTarget.style.transform = ''}
    >
      <picture style={{ position: 'absolute', inset: 0, display: 'block' }}>
        <source srcSet={`${card.img}.avif`} type="image/avif" />
        <source srcSet={`${card.img}.webp`} type="image/webp" />
        <img
          src={`${card.img}.jpg`}
          alt=""
          width="480"
          height="720"
          loading="eager"
          decoding="async"
          onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center top',
            display: 'block',
          }}
        />
      </picture>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, transparent 55%, rgba(5,5,5,0.9) 100%)',
      }}/>
      <div style={{
        position: 'absolute', left: 10, right: 10, bottom: 10,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'rgba(10,10,10,0.72)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        color: 'var(--fg1)',
        fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14,
        letterSpacing: '-0.01em',
      }}>
        <span>{card.label}</span>
        <svg width="14" height="14" viewBox="0 0 16 16" style={{ color: 'var(--fg2)' }}>
          <path d="M5 3l5 5-5 5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </button>
  );
}

// /2026 intro hero. Paladin-shaped: full-bleed photo background, eyebrow +
// headline + award/ratings badges overlay the top, photo bleeds through the
// middle, testimonial + Continue CTA + legal footer overlay the bottom.
// No data collection — s1 (screen-time 4-cards) is still next and owns capture.
//
// Assets live in /assets/intro-2026/:
//   intro-2026-bg.{avif,webp,jpg}   cinematic background photo (692×1500 source)
//   apple.svg                       Apple Design Awards badge (with laurels)
//   ratings.svg                     4.9★ 150K+ ratings badge (with laurels)
//
// Funnel-gated via `funnelOnly: ['2026', '2026-short', 'download', '2026-start', 'detox']`. The canonical
// screentime, families, and adhd funnels are unaffected.
const INTRO_2026_ASSETS = '/assets/intro-2026';
const HERO_HEADLINE_2026 = 'PLAN YOUR SOCIAL MEDIA DETOX';
const HERO_TESTIMONIAL_2026 = '"I cut my screen time in half. The only app that actually stuck."';

// `?hero=off` (also persisted to sessionStorage so the override carries
// across screens — the funnel does not propagate query params) disables the
// s0_hero intro and sends /2026 users straight to s1. Default: hero ON for
// all /2026 traffic. Mirrors the `?paywall=v1` escape hatch shipped with
// the paywall V2 rollout (see screens-phase8-v2.jsx).
const HERO_OVERRIDE_STORAGE_KEY = 'opal.heroOverride';

export function readHeroOverride({ search, storage } = {}) {
  const params = (() => {
    if (search) return new URLSearchParams(search);
    if (typeof window !== 'undefined' && window.location?.search) {
      return new URLSearchParams(window.location.search);
    }
    return null;
  })();
  const fromQuery = params?.get('hero') || null;
  const store = storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
  if (fromQuery) {
    try { store?.setItem(HERO_OVERRIDE_STORAGE_KEY, fromQuery); } catch {}
    return fromQuery;
  }
  try { return store?.getItem(HERO_OVERRIDE_STORAGE_KEY) || null; } catch { return null; }
}

export function isIntroHeroEnabled(opts) {
  // Hero is the production default. Only `?hero=off` (or a persisted 'off'
  // override) sends a user back to the legacy direct-to-s1 flow.
  return readHeroOverride(opts) !== 'off';
}

function Intro2026Hero({ onNext, progress, funnelCopy = {} }) {
  const title = funnelCopy.title || HERO_HEADLINE_2026;
  const testimonial = funnelCopy.testimonial || HERO_TESTIMONIAL_2026;
  return (
    <div style={{
      position: 'relative', minHeight: '100dvh',
      display: 'flex', flexDirection: 'column',
      background: '#050505',
      isolation: 'isolate',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
      }}>
        <picture style={{ display: 'block', width: '100%', height: '100%' }}>
          <source srcSet={`${INTRO_2026_ASSETS}/intro-2026-bg.avif`} type="image/avif" />
          <source srcSet={`${INTRO_2026_ASSETS}/intro-2026-bg.webp`} type="image/webp" />
          <img
            src={`${INTRO_2026_ASSETS}/intro-2026-bg.jpg`}
            alt=""
            width="692"
            height="1500"
            loading="eager"
            decoding="async"
            fetchpriority="high"
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              objectPosition: 'center 45%',
              display: 'block', pointerEvents: 'none', userSelect: 'none',
            }}
          />
        </picture>
        <div style={{
          position: 'absolute', inset: 0,
          background: `
            linear-gradient(180deg, rgba(5,5,5,0.78) 0%, rgba(5,5,5,0.22) 32%, rgba(5,5,5,0.22) 52%, rgba(5,5,5,0.94) 100%)`,
        }} />
      </div>

      <div style={{
        position: 'relative', zIndex: 1,
        padding: '22px 22px 0',
        display: 'flex', justifyContent: 'center',
      }}>
        <OpalWordmark height={26} />
      </div>

      <div style={{
        position: 'relative', zIndex: 1,
        padding: '18px 22px 0',
        textAlign: 'center',
      }}>
        <h1 style={{
          margin: 0,
          fontFamily: 'var(--font-display)', color: 'var(--fg1)',
          fontWeight: 600, letterSpacing: '0.01em',
          fontSize: 'clamp(28px, 7.4vw, 36px)', lineHeight: '1.06',
          textTransform: 'uppercase',
          textWrap: 'balance',
          maxWidth: '18ch', marginInline: 'auto',
          textShadow: '0 2px 18px rgba(0,0,0,0.7)',
        }}>{title}</h1>
      </div>

      <div style={{
        position: 'relative', zIndex: 1,
        padding: '16px 22px 0',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        gap: 16,
        filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.6))',
      }}>
        <img
          src={`${INTRO_2026_ASSETS}/apple.svg`}
          alt=""
          width="170" height="68"
          style={{ display: 'block', height: 50, width: 'auto' }}
        />
        <img
          src={`${INTRO_2026_ASSETS}/ratings.svg`}
          alt=""
          width="162" height="46"
          style={{ display: 'block', height: 36, width: 'auto' }}
        />
      </div>

      <div style={{ flex: 1, zIndex: 1, minHeight: 60 }} />

      <div style={{
        position: 'relative', zIndex: 1,
        padding: '0 26px 12px',
        textAlign: 'center',
        color: 'var(--fg1)',
        fontFamily: 'var(--font-display)',
        fontSize: 14, lineHeight: '20px',
        letterSpacing: '0.01em',
        textShadow: '0 2px 14px rgba(0,0,0,0.85)',
        maxWidth: '32ch', marginInline: 'auto',
      }}>
        <p style={{ margin: 0, fontStyle: 'italic' }}>{testimonial}</p>
      </div>

      <div style={{ position: 'relative', zIndex: 1, padding: '8px 22px 4px' }}>
        {/* start_polish_v1 exposure point: s0_hero is the first screen every
            2026-start user sees, so firing $exposure here tags every
            downstream event with the flag. Other funnels render plain
            "Continue" and stay out of the experiment. */}
        <PrimaryButton onClick={onNext}>{currentFunnelSlug() === '2026-start' ? (
          <Experiment flag="start_polish_v1">
            <Variant value="control">Continue</Variant>
            <Variant value="treatment">Get my plan</Variant>
            <Fallback>Continue</Fallback>
          </Experiment>
        ) : 'Continue'}</PrimaryButton>
      </div>
      <div style={{
        position: 'relative', zIndex: 1,
        padding: '6px 26px 18px', textAlign: 'center',
        color: 'rgba(255,255,255,0.80)', fontSize: 12, lineHeight: '16px',
        textShadow: '0 1px 8px rgba(0,0,0,0.85)',
      }}>
        By continuing, you agree to our
        {' '}<a href="#terms" style={{ color: 'var(--fg1)', textDecoration: 'underline' }}>Terms of Service</a>
        {' · '}<a href="#privacy" style={{ color: 'var(--fg1)', textDecoration: 'underline' }}>Privacy Policy</a>.
      </div>
    </div>
  );
}

// /get — one-screen App Store-style landing page. The App Store screenshots
// stacked vertically with a single "Try for free" CTA that opens the in-funnel
// paywall (get_paywall). No quiz, no data collection. Lives only on the /get
// funnel (funnelOnly below) so it never leaks into the quiz funnels.
const GET_SCREENSHOTS = [
  { src: '/assets/get/shot-1.png', alt: 'Opal blocks distractions for you.' },
  { src: '/assets/get/shot-2.png', alt: 'For better sleep, focus, and rest.' },
  { src: '/assets/get/shot-3.png', alt: 'Locks the apps you keep opening.' },
  { src: '/assets/get/shot-4.png', alt: 'Dive deeper into your progress.' },
  { src: '/assets/get/shot-5.png', alt: 'One tap. Total focus.' },
  { src: '/assets/get/shot-6.png', alt: 'Rules that run themselves.' },
  { src: '/assets/get/shot-7.png', alt: 'Routines for every part of your day.' },
];

function GetLanding({ onNext }) {
  // Warm RevenueCat on idle while the user reads the landing page so get_paywall
  // renders its prices instantly. No analytics fire here — Meta InitiateCheckout
  // now fires from the paywall's purchase button (the actual checkout
  // initiation), not on this landing click.
  useEffect(() => {
    const run = () => {
      (async () => {
        try {
          await window.OpalRC.initRC();
          await window.OpalRC.fetchPackages();
        } catch (_) { /* best-effort warm; paywall re-fetches on mount */ }
      })();
    };
    const hasIdle = typeof requestIdleCallback !== 'undefined';
    const id = hasIdle ? requestIdleCallback(run, { timeout: 2500 }) : setTimeout(run, 800);
    return () => {
      try { hasIdle ? cancelIdleCallback(id) : clearTimeout(id); } catch (_) {}
    };
  }, []);

  // CTA Clicked + cursor advance are handled by app.jsx next(); the click just
  // advances to the paywall. Meta InitiateCheckout is deliberately NOT fired
  // here anymore — it fires when the user actually initiates checkout on the
  // paywall (the purchase tap in GetPaywall.onPurchase).
  const onTryFree = () => { onNext?.(); };
  return (
    <div style={{
      position: 'relative',
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      background: 'radial-gradient(620px 420px at 50% -8%, rgba(158,249,255,0.16), transparent 62%), radial-gradient(520px 420px at 50% 110%, rgba(212,255,156,0.08), transparent 62%), #050505',
      color: '#fff',
    }}>
      <div style={{ padding: '24px 28px 8px', display: 'flex', justifyContent: 'center' }}>
        <OpalWordmark height={24} />
      </div>
      <main style={{
        flex: 1,
        width: '100%',
        maxWidth: 430,
        margin: '0 auto',
        padding: '20px 22px 8px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}>
        {GET_SCREENSHOTS.map((shot) => (
          <img
            key={shot.src}
            src={shot.src}
            alt={shot.alt}
            loading="lazy"
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              borderRadius: 24,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
            }}
          />
        ))}
      </main>
      <StickyFooter>
        <PrimaryButton
          onClick={onTryFree}
          style={{ background: '#fff', color: '#000', boxShadow: '0 12px 32px rgba(0,0,0,0.45)' }}
        >
          Try for free
        </PrimaryButton>
      </StickyFooter>
    </div>
  );
}

// Light-blue palette pulled from the App Store screenshots so the paywall and
// handoff feel like a continuation of the page rather than the dark quiz funnel.
const GET_BG = 'radial-gradient(125% 80% at 78% -12%, #eef5fc 0%, #d2e2f1 40%, #b4cce3 100%)';
const GET_INK = '#0e1c2c';
const GET_INK_SOFT = 'rgba(14,28,44,0.62)';

// Per-tab guard so InitiateCheckout (Meta) fires exactly once per session — on
// the paywall purchase tap (the actual checkout initiation). The guard stops a
// cancel/retry from firing a second InitiateCheckout within the same session.
const GET_IC_KEY = 'opal.get.ic_fired';
function getIcFired() {
  try { return sessionStorage.getItem(GET_IC_KEY) === '1'; } catch (_) { return false; }
}
function markIcFired() {
  try { sessionStorage.setItem(GET_IC_KEY, '1'); } catch (_) {}
}

function getPlanName(p) {
  const id = (p.identifier || '').toLowerCase();
  const lbl = (p.periodLabel || '').toLowerCase();
  if (/annual|year/.test(id) || /year/.test(lbl)) return 'Yearly';
  if (/month/.test(id) || /month/.test(lbl)) return 'Monthly';
  if (/week/.test(id) || /week/.test(lbl)) return 'Weekly';
  return p.title || 'Plan';
}

// Format a money amount with the package's real currency symbol (locale-aware),
// so per-day prices read "€0.19" / "$0.19" rather than a bare number.
function getFmtMoney(amount, currency) {
  try {
    return new Intl.NumberFormat(typeof navigator !== 'undefined' ? navigator.language : 'en-US', {
      style: 'currency', currency: currency || 'USD',
    }).format(amount);
  } catch (_) {
    return (currency || 'USD') + ' ' + amount.toFixed(2);
  }
}

// get_paywall — light-themed paywall for /get. Reuses the canonical RevenueCat
// plumbing (window.OpalRC.fetchPackages / purchasePackageById) so the offering,
// package order, and trial come from the funnel config (7-day-trial offering).
function GetPaywall({ state, patch, onNext, onBack }) {
  const funnel = (typeof window !== 'undefined' && window.__opalFunnel) || {};
  const trialCtaLabel = funnel.paywallTrialCtaLabel || 'Start 7 days for $0.00';

  const [packages, setPackages] = useState([]);
  const [offeringId, setOfferingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    window.OpalTrack?.capture?.('Web Funnel - Paywall Reached', { screen_id: 'get_paywall' });
    (async () => {
      try {
        await window.OpalRC.initRC();
        await window.OpalRC.attachIdentity(state.email);
        const res = await window.OpalRC.fetchPackages();
        if (cancelled) return;
        const pkgs = res.packages || [];
        setPackages(pkgs);
        setOfferingId(res.offeringId);
        // Default to the longest plan that carries the free trial (best value);
        // fall back to last, then first.
        const withTrial = pkgs.filter((p) => p.hasTrial);
        const def = (withTrial.sort((a, b) => (b.periodDays || 0) - (a.periodDays || 0))[0])
          || pkgs[pkgs.length - 1] || pkgs[0] || null;
        setSelectedId(def?.identifier || null);
        setLoading(false);
        // No InitiateCheckout fires on paywall render. Meta InitiateCheckout is
        // fired from onPurchase below (the actual checkout initiation). The
        // genuine "user saw the paywall" signal is 'Paywall Reached' above.
      } catch (e) {
        if (cancelled) return;
        setLoadError((e && e.message) || 'Could not load plans.');
        setLoading(false);
        window.OpalTrack?.capture?.('Web Funnel - Paywall Load Failed', {
          screen_id: 'get_paywall',
          error_message: String((e && e.message) || e || ''),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = packages.find((p) => p.identifier === selectedId) || packages[0] || null;
  const bestValueId = packages.length > 1
    ? packages.slice().sort((a, b) => (b.periodDays || 0) - (a.periodDays || 0))[0]?.identifier
    : null;

  const onSelect = (p) => {
    setSelectedId(p.identifier);
    window.OpalTrack?.capture?.('Web Funnel - Plan Selected', {
      screen_id: 'get_paywall', offering_identifier: offeringId,
      package_identifier: p.identifier, value: p.priceAmount || 0,
      currency: p.priceCurrency || 'USD', has_trial: !!p.hasTrial,
    });
  };

  const onPurchase = async () => {
    if (purchasing || !selected) return;
    setPurchasing(true);
    setPurchaseError(null);
    // /get's Amplitude "Paywall Viewed" — fired once per session on the purchase
    // tap (this funnel has no on-load paywall event). NOTE: Meta InitiateCheckout
    // is NOT driven from here anymore — it fires from 'Web Funnel - Checkout
    // Started' below, the uniform paywall-CTA-click mapping shared by all funnels.
    // Guarded once per session so a cancel/retry doesn't double-fire the event.
    if (!getIcFired()) {
      markIcFired();
      window.OpalTrack?.capture?.('Web Funnel - Paywall Viewed', {
        screen_id: 'get_paywall',
        offering_identifier: offeringId,
        packages_offered: packages.map((p) => p.identifier),
        default_selected_identifier: selected.identifier,
        package_identifier: selected.identifier,
        value: selected.priceAmount || 0,
        currency: selected.priceCurrency || 'USD',
        has_trial: !!selected.hasTrial,
      });
    }
    try {
      await window.OpalRC.attachIdentity(state.email);
      window.OpalTrack?.capture?.('Web Funnel - Checkout Started', {
        screen_id: 'get_paywall', offering_identifier: offeringId,
        package_identifier: selected.identifier, product_identifier: selected.productId,
        value: selected.priceAmount || 0,
        currency: selected.priceCurrency || 'USD', has_trial: !!selected.hasTrial,
      });
      const result = await window.OpalRC.purchasePackageById(selected.identifier, { email: state.email || undefined });
      window.OpalTrack?.capture?.('Web Funnel - Checkout Completed', {
        screen_id: 'get_paywall', offering_identifier: offeringId,
        package_identifier: selected.identifier, value: selected.priceAmount || 0,
        currency: selected.priceCurrency || 'USD', has_trial: !!selected.hasTrial,
        has_redeem_url: Boolean(result.redeemUrl),
      });
      // Meta StartTrial/Purchase (via META_STANDARD). Shares result.checkoutEventId
      // with the RC→Meta webhook backstop so the two collapse into one event.
      window.OpalTrack?.capture?.('Web Funnel - Subscription Activated', {
        screen_id: 'get_paywall',
        package_identifier: selected.identifier,
        product_identifier: result.productIdentifier || selected.productId,
        value: selected.priceAmount || 0,
        currency: selected.priceCurrency || 'USD',
        has_trial: !!selected.hasTrial,
        eventID: result.checkoutEventId,
      });
      patch({
        redeemUrl: result.redeemUrl,
        redemptionLinkMissing: !result.redeemUrl,
        purchasedPlan: selected.identifier,
        purchaseValue: selected.priceAmount || 0,
      });
      onNext?.();
    } catch (e) {
      const msg = String((e && e.message) || e || '');
      const cancelled = /cancel/i.test(msg) || (e && e.userCancelled);
      if (cancelled) {
        window.OpalTrack?.capture?.('Web Funnel - Checkout Cancelled', { screen_id: 'get_paywall' });
      } else {
        setPurchaseError((e && e.message) || 'Something went wrong. Please try again.');
        window.OpalTrack?.capture?.('Web Funnel - Checkout Failed', { screen_id: 'get_paywall', error_message: msg });
      }
    } finally {
      setPurchasing(false);
    }
  };

  const disabled = loading || !selected || purchasing;
  const ctaLabel = purchasing
    ? 'Starting…'
    : (selected && !selected.hasTrial
        ? ('Continue' + (selected.priceFormatted ? ' — ' + selected.priceFormatted : ''))
        : trialCtaLabel);
  const btnStyle = disabled
    ? { background: 'rgba(14,28,44,0.25)', color: '#fff', boxShadow: 'none' }
    : { background: GET_INK, color: '#fff', boxShadow: '0 12px 32px rgba(14,28,44,0.3)' };

  const PlanCardSkeleton = () => (
    <div style={{ height: 78, borderRadius: 18, background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(14,28,44,0.1)' }} />
  );

  return (
    <div style={{ position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: GET_BG, color: GET_INK }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '18px 20px 4px' }}>
        <button
          onClick={onBack || undefined}
          aria-label="Back"
          style={{
            width: 36, height: 36, borderRadius: 18, border: 'none',
            background: 'rgba(255,255,255,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: onBack ? 'pointer' : 'default', visibility: onBack ? 'visible' : 'hidden',
          }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" stroke={GET_INK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <OpalWordmark height={22} color={GET_INK} />
          <span style={{ fontSize: 11, fontWeight: 500, color: GET_INK_SOFT, letterSpacing: '0.01em' }}>
            Loved by 1 million daily users
          </span>
        </div>
        <span style={{ width: 36 }} />
      </div>

      <main style={{ flex: 1, width: '100%', maxWidth: 430, margin: '0 auto', padding: '14px 24px 8px', boxSizing: 'border-box' }}>
        <h1 style={{ margin: '8px 0 0', fontFamily: 'var(--font-display)', fontSize: 32, lineHeight: '36px', fontWeight: 700, letterSpacing: '-0.03em', textWrap: 'balance' }}>
          Start your free week &amp; gain 2+ hours back
        </h1>
        <p style={{ margin: '10px 0 0', fontSize: 16, lineHeight: '23px', color: GET_INK_SOFT }}>
          Block distracting apps, build focus routines, and reclaim your time — free for 7 days.
        </p>

        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading && (<><PlanCardSkeleton /><PlanCardSkeleton /></>)}
          {!loading && loadError && (
            <div style={{ padding: '16px 18px', borderRadius: 16, background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(14,28,44,0.14)', fontSize: 14, color: GET_INK }}>
              {loadError} Please refresh to try again.
            </div>
          )}
          {!loading && !loadError && packages.map((p) => {
            const isSel = selected && p.identifier === selected.identifier;
            const isBest = p.identifier === bestValueId;
            return (
              <button
                key={p.identifier}
                onClick={() => onSelect(p)}
                aria-pressed={isSel}
                style={{
                  position: 'relative', textAlign: 'left', width: '100%', padding: '16px 18px',
                  borderRadius: 18, cursor: 'pointer',
                  border: isSel ? `2px solid ${GET_INK}` : '1px solid rgba(14,28,44,0.14)',
                  background: isSel ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.6)',
                  boxShadow: isSel ? '0 10px 26px rgba(14,28,44,0.14)' : 'none',
                }}
              >
                {isBest && (
                  <span style={{ position: 'absolute', top: -10, right: 16, background: GET_INK, color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 9px', borderRadius: 9999 }}>
                    BEST VALUE
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>{getPlanName(p)}</div>
                    <div style={{ fontSize: 13, color: GET_INK_SOFT, marginTop: 3 }}>
                      {p.hasTrial ? '7-day free trial, then ' : ''}{p.priceFormatted || ''}{p.periodLabel || ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {p.perDayPrice != null && (
                      <div style={{ fontSize: 13, color: GET_INK_SOFT, whiteSpace: 'nowrap' }}>
                        {getFmtMoney(p.perDayPrice, p.priceCurrency)}/day
                      </div>
                    )}
                    <span aria-hidden="true" style={{
                      width: 22, height: 22, borderRadius: 11, boxSizing: 'border-box', flexShrink: 0,
                      border: isSel ? `7px solid ${GET_INK}` : '2px solid rgba(14,28,44,0.3)',
                    }} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {purchaseError && (
          <div style={{ marginTop: 12, color: '#b00020', fontSize: 13 }}>{purchaseError}</div>
        )}

        <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', borderRadius: 14, background: 'rgba(255,255,255,0.55)', border: '1px solid rgba(14,28,44,0.12)' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M12 2l7 3v6c0 4.5-3 8-7 11-4-3-7-6.5-7-11V5l7-3z" fill="rgba(14,28,44,0.1)" stroke={GET_INK} strokeWidth="1.4" />
            <path d="M8.7 12.2l2.2 2.2 4.4-4.6" fill="none" stroke={GET_INK} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: GET_INK }}>30-day money-back guarantee</div>
            <div style={{ fontSize: 12, color: GET_INK_SOFT, marginTop: 2 }}>Not for you? Get a full refund within 30 days.</div>
          </div>
        </div>

        <p style={{ marginTop: 16, fontSize: 12, lineHeight: '18px', color: 'rgba(14,28,44,0.5)' }}>
          {selected && selected.hasTrial
            ? `Free for 7 days, then ${selected.priceFormatted || ''}${selected.periodLabel || ''}. Cancel anytime before the trial ends and you won't be charged.`
            : 'Recurring subscription. Cancel anytime.'}
        </p>
      </main>

      <StickyFooter reserveHeight={150} style={{ background: 'linear-gradient(180deg, rgba(180,204,227,0) 0%, rgba(190,210,232,0.92) 42%, rgba(190,210,232,0.99) 100%)' }}>
        <PrimaryButton onClick={onPurchase} disabled={disabled} style={btnStyle}>{ctaLabel}</PrimaryButton>
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center', gap: 18, fontSize: 12, color: 'rgba(14,28,44,0.55)' }}>
          <a href="#terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms</a>
          <a href="#privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy</a>
        </div>
      </StickyFooter>
    </div>
  );
}

// get_handoff — light-themed redemption screen shown after a successful
// purchase. Mirrors s53's redeem logic (install + in-app redeem) in the /get
// palette so the post-checkout step stays on-brand.
function GetHandoff({ state }) {
  const APP_STORE = cfg.APP_STORE_FALLBACK || 'https://apps.apple.com/app/opal-screen-time-focus/id1497465230';
  const usableUrl = (v) => {
    if (!v || typeof v !== 'string') return null;
    try {
      const u = new URL(v, window.location.href);
      return ['https:', 'http:', 'itms-apps:'].includes(u.protocol) ? u.toString() : null;
    } catch (_) { return null; }
  };
  const stored = (window.OpalRC && window.OpalRC.lastPurchase && window.OpalRC.lastPurchase()) || null;
  const redeemUrl = usableUrl(state.redeemUrl || (stored && stored.redeemUrl) || null);
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');

  const openRedeem = () => {
    window.OpalTrack?.capture?.('Web Funnel - Redemption Link Clicked', { has_redeem_link: !!redeemUrl, screen_id: 'get_handoff' });
    if (!redeemUrl) return;
    if (isMobile) window.location.href = redeemUrl;
    else window.open(redeemUrl, '_blank', 'noopener');
  };

  const stepNumStyle = { width: 26, height: 26, borderRadius: 13, background: GET_INK, color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };

  return (
    <div style={{ position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: GET_BG, color: GET_INK }}>
      <div style={{ padding: '24px 28px 8px', display: 'flex', justifyContent: 'center' }}>
        <OpalWordmark height={24} color={GET_INK} />
      </div>
      <main style={{ flex: 1, width: '100%', maxWidth: 430, margin: '0 auto', padding: '30px 26px max(30px, env(safe-area-inset-bottom))', boxSizing: 'border-box' }}>
        <div style={{ display: 'inline-block', padding: '6px 12px', borderRadius: 9999, background: 'rgba(14,28,44,0.08)', color: GET_INK, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Trial active
        </div>
        <h1 style={{ margin: '14px 0 0', fontFamily: 'var(--font-display)', fontSize: 34, lineHeight: '38px', fontWeight: 700, letterSpacing: '-0.04em', textWrap: 'balance' }}>
          Welcome to Opal.
        </h1>
        <p style={{ margin: '12px 0 0', fontSize: 17, lineHeight: '25px', color: GET_INK_SOFT }}>
          Two quick steps to start your free trial in the app.
        </p>

        <div style={{ marginTop: 30, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={stepNumStyle}>1</div>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>Install the Opal app</div>
            <a href={APP_STORE} style={{ display: 'inline-flex', marginTop: 12 }} aria-label="Download Opal on the App Store">
              <img src="/assets/store-badges/app-store.svg" alt="Download on the App Store" style={{ height: 44, width: 'auto', display: 'block' }} />
            </a>
          </div>
        </div>

        <div style={{ marginTop: 28, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={stepNumStyle}>2</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>Redeem your subscription</div>
            <p style={{ margin: '8px 0 0', fontSize: 15, lineHeight: '22px', color: GET_INK_SOFT }}>
              {redeemUrl
                ? 'On your phone, tap below to open Opal and unlock Pro.'
                : 'Use the redemption email from checkout to activate Pro in Opal.'}
            </p>
            <button
              onClick={openRedeem}
              disabled={!redeemUrl}
              style={{
                marginTop: 14, minHeight: 50, padding: '0 22px', borderRadius: 9999, border: 'none',
                background: redeemUrl ? GET_INK : 'rgba(14,28,44,0.18)',
                color: '#fff', fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600,
                cursor: redeemUrl ? 'pointer' : 'not-allowed',
                boxShadow: redeemUrl ? '0 12px 30px rgba(14,28,44,0.25)' : 'none',
              }}
            >
              Open app and redeem
            </button>
          </div>
        </div>

        <p style={{ margin: '34px 0 0', fontSize: 12, lineHeight: '18px', color: 'rgba(14,28,44,0.5)' }}>
          Keep this page open until Pro is active in the app. Your 7-day free trial has started.
        </p>
      </main>
    </div>
  );
}

export default [
  // get_landing — one-screen App Store landing page (/get only). See GetLanding.
  {
    id: 'get_landing', key: 'get_landing', phase: 1, label: 'Get Landing',
    funnelOnly: ['get'],
    ctaLabel: 'Try for free',
    headline: 'Try Opal for free.',
    render: ({ onNext }) => <GetLanding onNext={onNext} />,
  },

  // get_paywall — light-themed 7-day-trial paywall (/get only). Manual paywall
  // milestone: fires its own 'Paywall Reached' (see GetPaywall).
  {
    id: 'get_paywall', key: 'get_paywall', phase: 1, label: 'Get Paywall', milestone: 'paywall',
    funnelOnly: ['get'],
    headline: 'Start your free week & gain 2+ hours back',
    render: ({ state, patch, onNext, onBack }) => (
      <GetPaywall state={state} patch={patch} onNext={onNext} onBack={onBack} />
    ),
  },

  // get_handoff — light-themed redemption screen (/get only). 'handoff'
  // milestone auto-fires 'Handoff Viewed' from app.jsx.
  {
    id: 'get_handoff', key: 'get_handoff', phase: 1, label: 'Get Handoff', milestone: 'handoff',
    funnelOnly: ['get'],
    headline: 'Welcome to Opal.',
    render: ({ state }) => <GetHandoff state={state} />,
  },

  // s0_hero — 2026 launch welcome hero. No data collection; just CTA → s1.
  // Copy lives in funnel screenOverrides.s0_hero so anyone forking the funnel
  // can override headline/kicker without editing the screen.
  //
  // Ships to 100% of /2026 traffic as of the rollout PR; the intro_image_hero_2026_v1
  // A/B test that previously gated this screen has been retired (see
  // docs/ab-testing.md §6 — Amplitude UI archive + follow-up file delete).
  // `?hero=off` (persisted in sessionStorage) is the escape hatch for QA /
  // support cases that need to revisit the legacy direct-to-s1 flow.
  {
    id: 's0_hero', phase: 1, label: 'Hero',
    funnelOnly: ['2026', '2026-short', 'download', '2026-start', 'detox', 'faith-christian'],
    enabled: () => isIntroHeroEnabled(),
    headline: HERO_HEADLINE_2026,
    headlineFromFunnelCopy: 'title',
    render: (props) => <Intro2026Hero {...props} />,
  },

  // s1 — Welcome entry. Default funnels keep the age-card landing;
  // /2026 switches this screen to a screen-time range question through funnelCopy.entryQuestion.
  {
    id: 's1', key: 'hook_age', phase: 1, label: 'Hook+Age',
    headline: 'Protect your time.',
    headlineFromFunnelCopy: 'title',
    answerField: 'age',
    render: ({ state, patch, onNext, progress, funnelCopy = {} }) => {
      const title = funnelCopy.title || 'Protect your time.';
      const kicker = funnelCopy.kicker || 'Based on your age';

      if (funnelCopy.entryQuestion === 'screenTimeRange') {
        const choose = (option) => {
          patch({
            dailyHoursRange: option.k,
            dailyHours: option.hours,
          });
          window.OpalTrack?.setUserProperty?.('onboarding_screen_time', screenTimeBucket(option.hours));
          window.OpalTrack?.setUserProperty?.('onboarding_daily_hours', option.hours);
          // Mirror the raw answerField onto the user record too — matches the
          // central dispatcher's "every quiz answer = user property" guarantee
          // (this path explicitly fires the event + suppresses the dispatcher,
          // so we have to do it inline).
          window.OpalTrack?.setUserProperty?.('dailyHoursRange', option.k);
          window.OpalTrack?.capture?.('Web Funnel - Quiz Step Answered', {
            question_key: 'dailyHoursRange',
            answer_value: option.k,
          });
          if (typeof window !== 'undefined') window.__opalSkipNextAnswered = true;
          onNext();
        };
        const chooseUnknown = () => {
          const fallbackHours = 5;
          patch({
            dailyHoursRange: 'unknown',
            dailyHours: fallbackHours,
          });
          window.OpalTrack?.setUserProperty?.('onboarding_screen_time', screenTimeBucket(fallbackHours));
          window.OpalTrack?.setUserProperty?.('onboarding_daily_hours', fallbackHours);
          window.OpalTrack?.capture?.('Web Funnel - Quiz Step Skipped', {
            question_key: 'dailyHoursRange',
          });
          if (typeof window !== 'undefined') window.__opalSkipNextAnswered = true;
          onNext();
        };
        return (
          <div style={{
            position: 'relative', minHeight: '100dvh',
            display: 'flex', flexDirection: 'column',
            background: `
              radial-gradient(700px 500px at 50% 10%, rgba(212,255,156,0.12), transparent 60%),
              radial-gradient(500px 500px at 50% 100%, rgba(158,249,255,0.06), transparent 60%),
              #050505`,
          }}>
            <div style={{
              padding: '18px 22px 0',
              display: 'flex', justifyContent: 'center',
            }}>
              <OpalWordmark height={22} />
            </div>
            <div style={{ flex: 1, padding: '26px 22px 0', display: 'flex', flexDirection: 'column' }}>
              <h1 style={{
                margin: 0, textAlign: 'center',
                fontFamily: 'var(--font-display)', fontWeight: 600,
                fontSize: 26, lineHeight: '30px',
                letterSpacing: '0.02em', color: 'var(--fg1)',
                textTransform: 'uppercase',
                textWrap: 'balance',
              }}>{currentFunnelSlug() === '2026' ? (
                <Experiment flag="welcome_2026_headline_v1">
                  <Variant value="control">{title}</Variant>
                  <Variant value="treatment">SCREEN TIME REDUCTION PLAN</Variant>
                  <Fallback>{title}</Fallback>
                </Experiment>
              ) : title}</h1>
              <div style={{
                marginTop: 8, textAlign: 'center',
                fontSize: 11, lineHeight: '14px', color: 'var(--fg3)',
                letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600,
              }}>{kicker}</div>

              <div style={{
                marginTop: 22,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}>
                {SCREEN_TIME_RANGE_OPTIONS.map((option) => (
                  <ScreenTimeRangeCard key={option.k} card={option} onClick={() => choose(option)} />
                ))}
              </div>
              <button
                onClick={chooseUnknown}
                style={{
                  marginTop: 14,
                  minHeight: 46,
                  width: '100%',
                  borderRadius: 999,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.045)',
                  color: 'var(--fg2)',
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: '-0.01em',
                  cursor: 'pointer',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.96)'}
                onMouseUp={(e) => e.currentTarget.style.transform = ''}
                onMouseLeave={(e) => e.currentTarget.style.transform = ''}
              >I don't know</button>
            </div>
            <div style={{ padding: '22px 26px 30px', textAlign: 'center' }}>
              <div style={{
                color: 'var(--fg3)', fontSize: 12, lineHeight: '18px',
              }}>
                By choosing your screen time and continuing, you agree to our
                {' '}<a href="#terms" style={{ color: 'var(--fg2)', textDecoration: 'underline' }}>Terms of Service</a>
                {' · '}<a href="#privacy" style={{ color: 'var(--fg2)', textDecoration: 'underline' }}>Privacy Policy</a>.
              </div>
            </div>
          </div>
        );
      }

      if (funnelCopy.entryQuestion === 'heavyApps') {
        const toggle = (k) => {
          const current = Array.isArray(state.heavyApps) ? state.heavyApps : [];
          const selected = new Set(current);
          selected.has(k) ? selected.delete(k) : selected.add(k);
          patch({ heavyApps: [...selected] });
        };
        const submit = () => {
          const picked = Array.isArray(state.heavyApps) && state.heavyApps.length > 0
            ? state.heavyApps
            : ['Other'];
          patch({ heavyApps: picked });
          window.OpalTrack?.setUserProperty?.('heavy_apps', picked);
          if (!state.heavyApps || state.heavyApps.length === 0) {
            window.OpalTrack?.capture?.('Web Funnel - Quiz Step Skipped', {
              question_key: 'heavyApps',
            });
            if (typeof window !== 'undefined') window.__opalSkipNextAnswered = true;
          }
          onNext();
        };
        return (
          <div style={{
            position: 'relative', height: '100dvh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            background: `
              radial-gradient(760px 520px at 50% 0%, rgba(212,255,156,0.14), transparent 62%),
              radial-gradient(520px 420px at 50% 100%, rgba(158,249,255,0.08), transparent 65%),
              #050505`,
          }}>
            <div style={{
              padding: '18px 22px 0',
              display: 'flex', justifyContent: 'center',
            }}>
              <OpalWordmark height={22} />
            </div>
            <div style={{
              flex: 1,
              minHeight: 0,
              padding: '26px 22px 10px',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <h1 style={{
                margin: 0, textAlign: 'center',
                fontFamily: 'var(--font-display)', fontWeight: 600,
                fontSize: 34, lineHeight: '38px',
                letterSpacing: '-0.035em', color: 'var(--fg1)',
                textWrap: 'balance',
              }}>{title}</h1>
              <div style={{
                margin: '10px auto 0', textAlign: 'center', maxWidth: 310,
                fontSize: 15, lineHeight: '21px', color: 'var(--fg2)',
                fontFamily: 'var(--font-text)', textWrap: 'pretty',
              }}>{kicker}</div>

              <div style={{
                marginTop: 24,
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                paddingBottom: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}>
                {LANDING_APP_OPTIONS.map((name) => (
                  <OptionTile key={name}
                    label={name}
                    icon={name === 'Other' ? '…' : AppIconFor(name)}
                    multi
                    selected={(state.heavyApps || []).includes(name)}
                    onClick={() => toggle(name)}
                  />
                ))}
              </div>
            </div>
            <StickyFooter reserveHeight={144}>
              <PrimaryButton onClick={submit}>Continue</PrimaryButton>
              <div style={{
                marginTop: 14,
                color: 'var(--fg3)', fontSize: 11, lineHeight: '16px',
                textAlign: 'center',
              }}>
                By continuing, you agree to our
                {' '}<a href="#terms" style={{ color: 'var(--fg2)', textDecoration: 'underline' }}>Terms</a>
                {' · '}<a href="#privacy" style={{ color: 'var(--fg2)', textDecoration: 'underline' }}>Privacy</a>.
              </div>
            </StickyFooter>
          </div>
        );
      }

      const choose = (k) => {
        patch({ age: k });
        // §3.4 demographic user property — sticks on the user record and
        // merges into the native session after signup.
        window.OpalTrack?.setUserProperty?.('age_range', k);
        // Mirror the raw answerField too — matches the dispatcher's contract
        // (every answer = user property). Dispatcher is suppressed below so
        // we have to do it inline here.
        window.OpalTrack?.setUserProperty?.('age', k);
        // Fire Quiz Step Answered explicitly: the tap-then-onNext path doesn't
        // give React time to flush state, so the central dispatcher in app.jsx
        // reads stateRef.current.age = null when next() runs. Set the suppress
        // flag so the dispatcher doesn't fire a second (empty) Answered event.
        window.OpalTrack?.capture?.('Web Funnel - Quiz Step Answered', {
          question_key: 'age',
          answer_value: k,
        });
        if (typeof window !== 'undefined') window.__opalSkipNextAnswered = true;
        onNext();
      };
      return (
        <div style={{
          position: 'relative', minHeight: '100dvh',
          display: 'flex', flexDirection: 'column',
          background: `
            radial-gradient(700px 500px at 50% 10%, rgba(212,255,156,0.12), transparent 60%),
            radial-gradient(500px 500px at 50% 100%, rgba(158,249,255,0.06), transparent 60%),
            #050505`,
        }}>
          <div style={{
            padding: '18px 22px 0',
            display: 'flex', justifyContent: 'center',
          }}>
            <OpalWordmark height={22} />
          </div>
          <div style={{ flex: 1, padding: '26px 22px 0', display: 'flex', flexDirection: 'column' }}>
            <h1 style={{
              margin: 0, textAlign: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 600,
              fontSize: 26, lineHeight: '30px',
              letterSpacing: '0.02em', color: 'var(--fg1)',
              textTransform: 'uppercase',
              textWrap: 'balance',
            }}>{title}</h1>
            <div style={{
              marginTop: 8, textAlign: 'center',
              fontSize: 11, lineHeight: '14px', color: 'var(--fg3)',
              letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600,
            }}>{kicker}</div>

            <div style={{
              marginTop: 22,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
            }}>
              {AGE_CARDS.map((c) => (
                <ImageOptionCard key={c.k} card={c} onClick={() => choose(c.k)} />
              ))}
            </div>
          </div>
          <div style={{ padding: '22px 26px 30px', textAlign: 'center' }}>
            <div style={{
              color: 'var(--fg3)', fontSize: 12, lineHeight: '18px',
            }}>
              By choosing your age and continuing, you agree to our
              {' '}<a href="#terms" style={{ color: 'var(--fg2)', textDecoration: 'underline' }}>Terms of Service</a>
              {' · '}<a href="#privacy" style={{ color: 'var(--fg2)', textDecoration: 'underline' }}>Privacy Policy</a>.
            </div>
          </div>
        </div>
      );
    },
  },

  // s4 — Social proof: Headway-inspired proof module, using Opal-specific traction.
  // In v1 (default) this screen is relocated to right before the paywall — see
  // socialProofRender export below, re-used from screens-phase8.jsx. The
  // `variant: 'pre_detox'` tag is what the detox-variant filter in app.jsx uses
  // to drop the early position when v1 is active.
  {
    id: 's4', key: 'social_proof', phase: 1, label: 'Social Proof', variant: 'pre_detox',
    headline: '1 million daily users',
    headlineFromFunnelCopy: 'headline',
    render: socialProofRender,
  },
];

// Exported so phase 8 can reuse the same render for the relocated v1 instance
// (between s48b and s49). Both call sites pass the same props.
export function socialProofRender({ onNext, onBack, progress, funnelCopy = {} }) {
      const headline = funnelCopy.headline || '1 million daily users';
      const subhead = funnelCopy.subhead || 'They use Opal to keep distracting apps from taking over their day.';
      return (
        <Screen
          progress={progress} onBack={onBack}
          onNext={onNext} ctaLabel="Continue"
        >
          <div style={{
            marginTop: 2,
            padding: '24px 18px 20px',
            borderRadius: 28,
            background: `
              radial-gradient(260px 180px at 50% 0%, rgba(212,255,156,0.16), transparent 70%),
              linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))`,
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07), 0 24px 70px rgba(0,0,0,0.28)',
            textAlign: 'center',
          }}>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 42,
              lineHeight: '46px',
              letterSpacing: '-0.045em',
              background: 'var(--opal-gradient-primary-h)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
              textWrap: 'balance',
            }}>{headline}</div>
            <p style={{
              margin: '8px auto 0', maxWidth: 300,
              fontSize: 15, lineHeight: '21px', color: 'var(--fg2)',
              textWrap: 'balance',
            }}>{subhead}</p>

            <div style={{
              marginTop: 28,
              textAlign: 'center',
            }}>
              <div style={{
                display: 'flex', justifyContent: 'center', marginBottom: 18,
              }}>
                <OpalAppIcon size={68} radius={0.24} />
              </div>
              <div style={{
                fontFamily: 'var(--font-display)', fontWeight: 600,
                color: 'var(--fg1)', fontSize: 22, lineHeight: '29px',
                letterSpacing: '-0.03em', textWrap: 'balance',
              }}>
                {(currentFunnelSlug() === '2026-start' || currentFunnelSlug() === 'faith-christian')
                  ? 'Opal blocks TikTok, Instagram, and Facebook during the moments that matter, so the impulse becomes futile.'
                  : 'Opal blocks TikTok and Instagram during work hours, so the impulse becomes futile.'}
              </div>
              <div style={{
                marginTop: 14, fontSize: 11, lineHeight: '15px',
                color: 'var(--fg3)', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600,
              }}>
                The New York Times / Wirecutter
              </div>
            </div>

            <div style={{
              marginTop: 24,
              fontSize: 10,
              color: 'var(--fg3)',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}>Mentioned in</div>
            <div style={{
              marginTop: 14,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '13px 18px',
              alignItems: 'center',
              justifyItems: 'center',
              opacity: 0.82,
              filter: 'grayscale(1)',
            }}>
              <PressLogo name="apple" size={18} />
              <PressLogo name="nyt" size={13} />
              <PressLogo name="wsj" size={16} />
              <PressLogo name="tc" size={18} />
            </div>
          </div>

          <div style={{
            marginTop: 18,
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
          }}>
            <TrustMetric value="200M+" label="hours saved from screens" />
            <TrustMetric value="1h 23m" label="average daily time saved" />
          </div>
        </Screen>
      );
}

function TrustMetric({ value, label }) {
  return (
    <div style={{
      padding: '13px 10px',
      borderRadius: 16,
      background: 'var(--ios-glass-5)',
      border: '1px solid var(--ios-hairline)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
      textAlign: 'center',
    }}>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize: 22,
        lineHeight: '25px',
        letterSpacing: '-0.035em',
        color: 'var(--fg1)',
      }}>{value}</div>
      <div style={{
        marginTop: 4,
        fontSize: 11,
        lineHeight: '14px',
        color: 'var(--fg3)',
        textWrap: 'balance',
      }}>{label}</div>
    </div>
  );
}
